import { useEffect, useState } from "react";
import { Leaf, Sprout, Star } from "lucide-react";
import { GameIcon } from "./GameTarget";
import { PROJECTILE_FR } from "./Projectile";
import { ReadAloud } from "../brand/Experience";
import { Portrait } from "./savanna/SavannaOverlays";

export const Overlay = ({ children, dark = true }) => (
  <div className={`absolute inset-0 z-40 safe-bottom flex items-end p-4 pb-8 ${dark ? "bg-[#061A2E]/55" : "bg-transparent"} backdrop-blur-[2px]`}>
    <div className="w-full">{children}</div>
  </div>
);

const Primary = ({ children, testId, ...p }) => (
  <button data-testid={testId} {...p} className="mt-5 h-16 w-full rounded-full bg-brand text-lg font-extrabold text-white shadow-lg shadow-[#17A2A2]/30 transition-transform active:scale-[.98] disabled:opacity-40">{children}</button>
);

export const ElderIntro = ({ onNext }) => (
  <div className="rounded-3xl bg-white p-6 shadow-2xl" data-testid="elder-intro">
    <div className="flex items-center gap-3">
      <Portrait who="aissa" />
      <div><p className="text-xs font-bold tracking-[0.25em] text-[#0F7C7C]">MAMA AÏSSA · L'ANCIENNE</p><p className="text-sm text-slate-500">Bao se réveille au village…</p></div>
    </div>
    <p className="mt-4 font-display text-xl font-bold leading-snug text-[#0A2540]">
      « Le Grand Baobab perd sa force. Les Graines de Vie ont disparu. Nous avons besoin de toi, Bao. »
    </p>
    <p className="mt-3 text-sm text-slate-600">Prépare ton voyage : rassemble ce dont tu as besoin dans le village.</p>
    <div className="mt-3"><ReadAloud testId="elder-read-aloud" text="Le Grand Baobab perd sa force. Les Graines de Vie ont disparu. Nous avons besoin de toi, Bao. Prépare ton voyage." /></div>
    <Primary testId="elder-intro-continue" onClick={onNext}>Je suis prêt</Primary>
  </div>
);

export const Briefing = ({ mission, index, total, onGo }) => (
  <div className="rounded-3xl bg-white p-6 shadow-2xl" data-testid={`mission-briefing-${mission.key}`}>
    <div className="flex items-center gap-4">
      <div className="rounded-2xl bg-[#EAF7F5] p-1"><GameIcon kind={mission.key} size={64} /></div>
      <div><p className="text-xs font-bold tracking-[0.25em] text-[#0F7C7C]">MISSION {index + 1} / {total}</p>
        <p className="font-display text-2xl font-extrabold text-[#0A2540]">{mission.name_fr}</p></div>
    </div>
    <p className="mt-4 text-lg leading-snug text-[#0A2540]">{mission.story_fr}</p>
    {mission.projectile && mission.projectile !== "none" && (
      <p data-testid="briefing-projectile" className="mt-2 text-base font-semibold text-[#B45309]">Arme : {PROJECTILE_FR[mission.projectile]} — vise avec ta main, garde la visée, puis relâche pour tirer.</p>
    )}
    <div className="mt-3"><ReadAloud testId="briefing-read-aloud" text={`${mission.name_fr}. ${mission.story_fr}${mission.projectile && mission.projectile !== "none" ? " Vise avec ta main, garde la visée, puis relâche pour tirer." : ""}`} /></div>
    <p className="mt-3 rounded-xl bg-[#EAF7F5] px-3 py-2 text-xs font-semibold text-[#0F7C7C]">Mouvement : {mission.movement_fr} · {mission.targets.length} répétitions</p>
    <Primary testId="mission-start-button" onClick={onGo}>C'est parti !</Primary>
  </div>
);

export const RestCard = ({ result, restS, onNext }) => {
  const [left, setLeft] = useState(restS);
  useEffect(() => { if (left <= 0) return; const id = setTimeout(() => setLeft(left - 1), 1000); return () => clearTimeout(id); }, [left]);
  return (
    <div className="rounded-3xl bg-white p-6 text-center shadow-2xl" data-testid="rest-card">
      <p className="text-xs font-bold tracking-[0.25em] text-[#0F7C7C]">MISSION RÉUSSIE</p>
      <div className="mt-2 flex justify-center gap-1" data-testid="mission-stars">
        {[1, 2, 3].map((i) => <Star key={i} className={`h-10 w-10 ${i <= (result?.stars || 0) ? "fill-[#E0B33A] text-[#E0B33A]" : "text-slate-300"}`} />)}
      </div>
      <p className="mt-2 flex items-center justify-center gap-2 font-semibold text-[#2BA36B]"><Leaf className="h-5 w-5" /> +1 feuille pour le Baobab</p>
      <p className="mt-4 font-display text-xl font-bold">Bao se repose un instant</p>
      <p className="text-sm text-slate-500">Respire calmement, relâche tes épaules.</p>
      <p data-testid="rest-countdown" className="mt-3 font-mono text-4xl font-bold text-[#17A2A2]">{left > 0 ? `${left}s` : "Prêt !"}</p>
      <Primary testId="rest-next-button" disabled={left > 0} onClick={onNext}>Mission suivante</Primary>
    </div>
  );
};

export const LevelComplete = ({ totals, t, onEnd }) => (
  <div className="rounded-3xl bg-[#0A2540]/90 p-6 text-center text-white shadow-2xl backdrop-blur-md" data-testid="level-complete">
    <div className="leaf-grow mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-brand shadow-[0_0_60px_#17A2A2]">
      <Sprout className="h-14 w-14" />
    </div>
    <p className="mt-4 text-sm text-white/70">Une feuille repousse sur le Grand Baobab…</p>
    <h2 className="mt-1 font-display text-3xl font-extrabold">{t("seedFound")}</h2>
    <p data-testid="seed-reward" className="font-display text-2xl font-bold text-[#E0B33A]">{t("plusSeed")}</p>
    <div className="mt-3 flex justify-center gap-6 text-sm">
      <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-[#E0B33A] text-[#E0B33A]" />{totals.stars} étoiles</span>
      <span className="flex items-center gap-1"><Leaf className="h-4 w-4 text-[#4FD1C5]" />{totals.leaves} feuilles</span>
    </div>
    <Primary testId="level-finish-button" onClick={onEnd}>Terminer la séance</Primary>
  </div>
);
